/**
 * タイトルスタッキーコンテンツのスクロール制御を管理（デバッグログ付き）
 *
 * 仕様:
 * - .ist-titleStickyContent__headerInner: ヘッダー内部
 * - .ist-titleStickyContent__body: ボディ
 *
 * 動作:
 * - ボディの内容がブラウザ内の高さを超えている場合、ヘッダー内部にスクロールバーを発生させるためのクラス（例：ist-titleStickyContent__headerInner--overflow）を付与する。
 *
 * 使い方:
 * <div class="ist-titleStickyContent">
 *   <section class="ist-titleStickyContent__item">
 *     <header class="ist-titleStickyContent__header">
 *       <div class="ist-titleStickyContent__headerInner">
 *         ヘッダーの内容
 *       </div>
 *     </header>
 *     <div class="ist-titleStickyContent__body">
 *       ボディの内容
 *     </div>
 *   </section>
 * </div>
 */
export class StickyContentManager {
  private container: Element;
  private headerInner: HTMLElement | null;
  private body: HTMLElement | null;
  private boundUpdateScrollStatus: () => void;
  private debug: boolean;

  constructor(container: Element, debug: boolean = false) {
    this.container = container;
    this.headerInner = container.querySelector(
      '.ist-titleStickyContent__headerInner'
    );
    this.body = container.querySelector('.ist-titleStickyContent__body');
    this.boundUpdateScrollStatus = this.updateScrollStatus.bind(this);
    this.debug = debug;
    this.init();
  }

  private init(): void {
    if (!this.headerInner || !this.body) {
      if (this.debug) {
        console.warn(
          'StickyContentManager init: 必要な要素が見つかりませんでした。',
          this.container
        );
      }
      return;
    }
    if (this.debug) {
      console.log('StickyContentManager initialized for', this.container);
    }

    // 初期状態のスクロール状態を更新
    this.updateScrollStatus();

    // ウィンドウのリサイズ時にスクロール状態を更新
    window.addEventListener('resize', this.boundUpdateScrollStatus);
  }

  private updateScrollStatus(): void {
    if (!this.headerInner || !this.body) return;

    // .ist-titleStickyContent__body の scrollHeight とブラウザの高さを比較
    const bodyScrollHeight = this.body.scrollHeight;
    const viewportHeight = window.innerHeight;
    if (this.debug) {
      console.log(
        'updateScrollStatus:',
        { bodyScrollHeight, viewportHeight },
        this.container
      );
    }

    if (
      bodyScrollHeight > viewportHeight &&
      this.headerInner.scrollHeight > viewportHeight
    ) {
      // オーバーフローしている場合、スクロールバー用クラスを追加
      this.headerInner.classList.add(
        'ist-titleStickyContent__headerInner--overflow'
      );
      if (this.debug) {
        console.log('スクロールバークラスを追加', this.headerInner);
      }
    } else {
      // 全部表示されている場合、クラスを削除
      this.headerInner.classList.remove(
        'ist-titleStickyContent__headerInner--overflow'
      );
      if (this.debug) {
        console.log('スクロールバークラスを削除', this.headerInner);
      }
    }
  }

  public destroy(): void {
    window.removeEventListener('resize', this.boundUpdateScrollStatus);
  }
}

/**
 * 複数のタイトルスタッキーコンテンツを管理するマネージャークラス
 */
export class StickyContentManagerGroup {
  private managers: StickyContentManager[] = [];
  private debug: boolean;

  constructor(debug: boolean = false) {
    this.debug = debug;
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        if (this.debug) {
          console.log('DOMContentLoaded: StickyContentManagerGroupの初期化');
        }
        this.initializeManagers();
      });
    } else {
      if (this.debug) {
        console.log('StickyContentManagerGroup init: DOMは既に読み込み済み');
      }
      this.initializeManagers();
    }
  }

  private initializeManagers(): void {
    const containers = document.querySelectorAll('.ist-titleStickyContent');
    if (this.debug) {
      console.log(
        'StickyContentManagerGroup: 対象のコンテナ数',
        containers.length
      );
    }
    containers.forEach((container) => {
      this.managers.push(new StickyContentManager(container, this.debug));
    });
  }

  public getManagers(): StickyContentManager[] {
    return this.managers;
  }

  public destroy(): void {
    this.managers.forEach((manager) => manager.destroy());
    this.managers = [];
  }
}
